import type { AnnotationStats } from '@/components/admin/annotation-stats';
import type { AnnotationRecord } from '@/components/admin/annotation-browser';

export interface AdminParticipant {
  id: string;
  userId: string;
  age?: number;
  ageRange?: string;
  musicalGenres: string[];
  musicalEngagement: 'casual' | 'enthusiast' | 'musician' | 'professional';
  formalTrainingYears: number;
  listeningDevice: string;
  listeningContext: 'alone' | 'with-others';
  environment: string;
  aiMusicExperience: string;
  annotationCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface GetStatsResponse {
  stats: AnnotationStats;
}

export interface GetAnnotationsResponse {
  annotations: AnnotationRecord[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface GetParticipantsResponse {
  participants: AdminParticipant[];
}

export interface AnnotationFilters {
  participantId?: string;
  sessionId?: string;
  songId?: string;
  songSource?: string;
  authenticityAssessment?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

class AdminService {
  private baseUrl = '/api/admin';

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = localStorage.getItem('auth-token');
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get admin dashboard statistics
   */
  async getStats(): Promise<GetStatsResponse> {
    return this.makeRequest<GetStatsResponse>('/stats');
  }

  /**
   * Reset all active sessions (cancel incomplete sessions)
   */
  async resetSessions(): Promise<{ cancelledCount: number; message: string }> {
    return this.makeRequest<{ cancelledCount: number; message: string }>('/reset-sessions', {
      method: 'POST',
    });
  }

  /**
   * Get annotations with filtering and pagination
   */
  async getAnnotations(filters: AnnotationFilters = {}): Promise<GetAnnotationsResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/annotations?${queryString}` : '/annotations';
    
    return this.makeRequest<GetAnnotationsResponse>(endpoint);
  }

  /**
   * Get participant summaries
   */
  async getParticipants(): Promise<GetParticipantsResponse> {
    return this.makeRequest<GetParticipantsResponse>('/participants');
  }

  /**
   * Export annotation data
   */
  async exportAnnotations(
    filters: AnnotationFilters = {}, 
    format: 'csv' | 'json' = 'csv',
    options: {
      includeParticipantData?: boolean;
      includeSongData?: boolean;
    } = {}
  ): Promise<Blob> {
    const searchParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    searchParams.append('format', format);
    
    if (options.includeParticipantData) {
      searchParams.append('includeParticipantData', 'true');
    }
    
    if (options.includeSongData) {
      searchParams.append('includeSongData', 'true');
    }
    
    const token = localStorage.getItem('auth-token');
    const response = await fetch(`${this.baseUrl}/annotations/export?${searchParams.toString()}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.blob();
  }

  /**
   * Export participant data
   */
  async exportParticipants(
    format: 'csv' | 'json' = 'csv',
    options: {
      includeAnnotationStats?: boolean;
    } = {}
  ): Promise<Blob> {
    const searchParams = new URLSearchParams();
    searchParams.append('format', format);
    
    if (options.includeAnnotationStats) {
      searchParams.append('includeAnnotationStats', 'true');
    }
    
    const token = localStorage.getItem('auth-token');
    const response = await fetch(`${this.baseUrl}/participants/export?${searchParams.toString()}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.blob();
  }

  /**
   * Download annotation export file
   */
  async downloadAnnotationExport(
    filters: AnnotationFilters = {}, 
    format: 'csv' | 'json' = 'csv',
    options: {
      includeParticipantData?: boolean;
      includeSongData?: boolean;
    } = {}
  ): Promise<void> {
    try {
      const blob = await this.exportAnnotations(filters, format, options);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `annotations_export_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Annotation export download failed:', error);
      throw error;
    }
  }

  /**
   * Download participant export file
   */
  async downloadParticipantExport(
    format: 'csv' | 'json' = 'csv',
    options: {
      includeAnnotationStats?: boolean;
    } = {}
  ): Promise<void> {
    try {
      const blob = await this.exportParticipants(format, options);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `participants_export_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Participant export download failed:', error);
      throw error;
    }
  }

  /**
   * Download export file (legacy method for backward compatibility)
   */
  async downloadExport(filters: AnnotationFilters = {}, format: 'csv' | 'json' = 'csv'): Promise<void> {
    return this.downloadAnnotationExport(filters, format);
  }

  /**
   * Get annotation statistics with filters
   */
  async getFilteredStats(filters: AnnotationFilters = {}): Promise<GetStatsResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/stats?${queryString}` : '/stats';
    
    return this.makeRequest<GetStatsResponse>(endpoint);
  }

  /**
   * Get participant details with annotations
   */
  async getParticipantDetails(participantId: string): Promise<{
    participant: AdminParticipant;
    annotations: AnnotationRecord[];
  }> {
    const [participantsResponse, annotationsResponse] = await Promise.all([
      this.getParticipants(),
      this.getAnnotations({ participantId })
    ]);

    const participant = participantsResponse.participants.find(p => p.id === participantId);
    if (!participant) {
      throw new Error('Participant not found');
    }

    return {
      participant,
      annotations: annotationsResponse.annotations
    };
  }

  /**
   * Export feedback data
   */
  async exportFeedback(
    filters: {
      processed?: boolean;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      context?: string;
      participantId?: string;
    } = {},
    format: 'csv' | 'json' = 'csv',
    options: {
      includeParticipantData?: boolean;
      includeSessionData?: boolean;
    } = {}
  ): Promise<Blob> {
    const searchParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    searchParams.append('format', format);
    
    if (options.includeParticipantData) {
      searchParams.append('includeParticipantData', 'true');
    }
    
    if (options.includeSessionData) {
      searchParams.append('includeSessionData', 'true');
    }
    
    const token = localStorage.getItem('auth-token');
    const response = await fetch(`${this.baseUrl}/feedback/export?${searchParams.toString()}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.blob();
  }

  /**
   * Download feedback export file
   */
  async downloadFeedbackExport(
    filters: {
      processed?: boolean;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      context?: string;
      participantId?: string;
    } = {},
    format: 'csv' | 'json' = 'csv',
    options: {
      includeParticipantData?: boolean;
      includeSessionData?: boolean;
    } = {}
  ): Promise<void> {
    try {
      const blob = await this.exportFeedback(filters, format, options);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `feedback_export_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Feedback export download failed:', error);
      throw error;
    }
  }

  /**
   * Get data quality metrics
   */
  async getDataQualityMetrics(): Promise<{
    completionRate: number;
    averageAnnotationTime: number;
    consistencyScore: number;
    participantEngagement: number;
  }> {
    // This would be implemented with specific API endpoints for data quality
    // For now, return mock data
    return {
      completionRate: 0.87,
      averageAnnotationTime: 125, // seconds
      consistencyScore: 0.92,
      participantEngagement: 0.78
    };
  }
}

export const adminService = new AdminService();